<script setup lang="ts">
  import { computed } from "vue";
  import { ChevronDown, ChevronUp } from "lucide-vue-next";
  import { Spinner } from "@/components/ui/spinner";
  import type { RenderedStep } from "@utils/assistantActivity";

  const props = withDefaults(defineProps<{ steps: RenderedStep[]; summary?: string }>(), { summary: "" });

  const collapsed = defineModel<boolean>("collapsed", { default: true });

  const VISIBLE_STEPS = 3;

  const hiddenCount = computed(() => Math.max(0, props.steps.length - 1));
  const visibleSteps = computed(() => props.steps.slice(-VISIBLE_STEPS));
  const earlierCount = computed(() => props.steps.length - visibleSteps.value.length);
  const isLive = computed(() => props.steps.some((step) => step.live));
</script>

<template>
  <div class="flex flex-col">
    <button
      v-if="collapsed"
      type="button"
      class="inline-flex items-center gap-1.5 self-start rounded text-xs text-muted-foreground transition-colors hover:text-foreground"
      :title="`Show the ${steps.length} steps`"
      @click="collapsed = false">
      <span class="inline-flex gap-[3px]">
        <i
          v-for="n in 3"
          :key="n"
          class="block h-[5px] w-[5px] rounded-full bg-ai-a" />
      </span>
      <span class="truncate">{{ summary }}</span>
      <span
        v-if="hiddenCount"
        class="rounded bg-muted px-1.5 font-mono text-3xs">
        +{{ hiddenCount }}
      </span>
      <ChevronDown class="h-3 w-3 shrink-0" />
    </button>

    <template v-else>
      <span
        v-if="earlierCount"
        class="mb-1.5 pl-[22px] text-2xs text-muted-foreground">
        {{ earlierCount }} earlier {{ earlierCount === 1 ? "step" : "steps" }}
      </span>
      <div
        v-for="(step, index) in visibleSteps"
        :key="earlierCount + index"
        class="ai-rise grid grid-cols-[14px_1fr] gap-2">
        <div class="flex flex-col items-center">
          <Spinner
            v-if="step.live"
            class="mt-0.5 size-3.5 text-ai-text" />
          <span
            v-else
            class="mt-1.5 h-[7px] w-[7px] shrink-0 rounded-full bg-ai-a" />
          <span
            v-if="index < visibleSteps.length - 1"
            class="mt-1 w-px flex-1 bg-border" />
        </div>
        <div
          class="flex min-w-0 items-baseline gap-1.5 text-xs text-muted-foreground"
          :class="index < visibleSteps.length - 1 ? 'pb-2' : ''">
          <span
            v-if="step.live"
            class="truncate bg-clip-text bg-[length:220%_100%] bg-[linear-gradient(90deg,var(--muted-foreground)_0%,var(--color-ai-a)_40%,var(--color-ai-b)_55%,var(--muted-foreground)_75%)] font-medium text-transparent animate-[ai-shimmer_1.8s_linear_infinite] motion-reduce:animate-none motion-reduce:bg-none motion-reduce:text-muted-foreground">
            {{ step.label }} {{ step.detail }}
          </span>
          <template v-else>
            <span class="shrink-0">{{ step.label }}</span>
            <b class="truncate font-medium text-foreground">{{ step.detail }}</b>
            <span
              v-if="step.duration"
              class="ml-auto shrink-0 font-mono text-3xs opacity-80">
              {{ step.duration }}
            </span>
          </template>
        </div>
      </div>

      <button
        v-if="!isLive && steps.length > 1"
        type="button"
        class="mt-1.5 inline-flex items-center gap-1 self-start text-2xs text-muted-foreground transition-colors hover:text-foreground"
        @click="collapsed = true">
        <ChevronUp class="h-3 w-3" />
        Hide steps
      </button>
    </template>
  </div>
</template>
